import React from "react";

import { GOOSE_GAME_POINTS_ICON_SHAPE } from "src/shared/constants/gooseGames";
import { globalConstants } from "src/theme/globalConstants";

export type PointsIconVariant = "inherit" | "primary" | "gray" | "white";
export type PointsIconShape = "gem" | "luma";

export interface PointsIconProps extends React.SVGProps<SVGSVGElement> {
  /** A semantic color. Use `color` for a one-off custom color. */
  variant?: PointsIconVariant;
  /** The event-specific symbol used to represent points. */
  shape?: PointsIconShape;
  /** Sets both width and height unless either is supplied separately. */
  size?: number | string;
}

const VARIANT_COLORS: Record<PointsIconVariant, string> = {
  inherit: "currentColor",
  primary: String(globalConstants.color!.brandPrimary),
  gray: String(globalConstants.color!.textSecondary),
  white: String(globalConstants.color!.white),
};

const GEM_OUTER_PATH =
  "M10.25 4.5L8.35653 4.92077C8.12628 4.97194 7.92153 5.10284 7.77845 5.29036L2 12.8636L14.5 20.1816L21 7.68182L12.7977 4.12895C12.6042 4.04513 12.3892 4.02463 12.1833 4.07038L10.25 4.5Z";

const GEM_FACET_PATH =
  "M14.5 20.1816L15.5 9.18182M14.5 20.1816L7 11.5M21 7.68182L15.5 9.18182M2 12.8636L7 11.5M15.5 9.18182L11.5 10.2727L7 11.5M15.5 9.18182L10.25 4.5M7 11.5L10.25 4.5";

const LUMA_PATH =
  "M13.8616 6.11106C13.4857 4.83812 12.5137 3.7637 11.1586 3.31357C8.95917 2.583 6.58403 3.7737 5.85342 5.97315C5.40329 7.32825 5.6826 8.74989 6.47766 9.81273C5.20467 10.1886 4.13022 11.1609 3.68009 12.516C2.94957 14.7154 4.14024 17.0906 6.33966 17.8212C7.69482 18.2714 9.11648 17.9918 10.1793 17.1967C10.5552 18.4697 11.5271 19.5443 12.8822 19.9945C15.0817 20.7251 17.4571 19.5344 18.1877 17.335C18.6379 15.9798 18.3585 14.5579 17.5633 13.495C18.8362 13.1192 19.9108 12.1474 20.361 10.7924C21.0916 8.59295 19.9009 6.21755 17.7015 5.48691C16.3464 5.03677 14.9245 5.31598 13.8616 6.11106ZM15.189 12.6933C14.6407 14.4298 12.7885 15.3931 11.052 14.8448C9.31548 14.2965 8.35225 12.4443 8.90055 10.7078C9.44885 8.97129 11.301 8.00806 13.0375 8.55636C14.774 9.10466 15.7373 10.9568 15.189 12.6933Z";

export const PointsIcon: React.FC<PointsIconProps> = ({
  variant = "inherit",
  shape = GOOSE_GAME_POINTS_ICON_SHAPE,
  size = 24,
  width,
  height,
  color,
  ...svgProps
}) => {
  const iconColor = color || VARIANT_COLORS[variant];

  return (
    <svg
      {...svgProps}
      width={width || size}
      height={height || size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden={svgProps["aria-label"] ? undefined : true}
    >
      {shape === "gem" ? (
        <>
          <path
            d={GEM_OUTER_PATH}
            stroke={iconColor}
            strokeWidth={1.5}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d={GEM_FACET_PATH}
            stroke={iconColor}
            strokeWidth={1.5}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </>
      ) : (
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d={LUMA_PATH}
          stroke={iconColor}
          strokeWidth={1.5}
          strokeLinejoin="round"
        />
      )}
    </svg>
  );
};
